import { useState } from "react";
import { FileText, Loader2, Search, Download, ExternalLink, RefreshCw } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

/**
 * Admin view of every signed Master Agent Participation Agreement.
 *
 * This exists because of the client's own example: "if I need to find an
 * agreement from April, I should be able to search the associate name and
 * immediately locate the record". Signed agreements were previously reachable
 * only one associate at a time, through the Reps tab, with no search anywhere.
 *
 * Search and paging are SERVER-side across the whole table, so results never
 * depend on what the browser has already loaded.
 */
const PAGE_SIZE = 25;

export default function SignedAgreementsPanel() {
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(0);

  const debouncedSearch = useDebouncedValue(search, 400);

  const query = trpc.agreement.adminSearch.useQuery(
    {
      query: debouncedSearch.trim() || undefined,
      from: from || undefined,
      to: to || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    },
    { staleTime: 30_000, placeholderData: prev => prev },
  );

  // Any filter change invalidates the current page offset.
  const resetTo = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(0); };

  const rows = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min((page + 1) * PAGE_SIZE, total);
  const hasFilters = !!(debouncedSearch.trim() || from || to);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Signed Agreements</h2>
          <p className="text-sm text-gray-400 mt-0.5">
            Every signed Master Agent Participation Agreement. Search covers all records, not just those on this page.
          </p>
        </div>
        <button
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          {query.isFetching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by associate name, email, rep code, or signed name…"
            value={search}
            onChange={e => resetTo(setSearch)(e.target.value)}
            className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#84ab4a]/60 focus:ring-2 focus:ring-[#84ab4a]/20 bg-white"
          />
          {query.isFetching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-gray-400" />
          )}
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Signed from</label>
          <input
            type="date"
            value={from}
            onChange={e => resetTo(setFrom)(e.target.value)}
            className="text-xs px-3 py-2 border border-gray-200 rounded-xl bg-white text-gray-600 focus:outline-none focus:border-[#84ab4a]/60"
          />
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">to</label>
          <input
            type="date"
            value={to}
            onChange={e => resetTo(setTo)(e.target.value)}
            className="text-xs px-3 py-2 border border-gray-200 rounded-xl bg-white text-gray-600 focus:outline-none focus:border-[#84ab4a]/60"
          />
        </div>
        {hasFilters && (
          <button
            onClick={() => { setSearch(""); setFrom(""); setTo(""); setPage(0); }}
            className="text-xs text-gray-400 hover:text-gray-600 underline py-2"
          >
            Clear
          </button>
        )}
      </div>

      {query.isLoading ? (
        <div className="py-12 text-center text-gray-400 text-sm flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading signed agreements…
        </div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-gray-400 text-sm">
          {hasFilters ? "No signed agreements match those filters." : "No agreements have been signed yet."}
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 border-b border-gray-100">
              <tr>
                {["Associate", "Rep Code", "Signed by", "Date signed", "Issue", "Agreement"].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any) => (
                <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">
                      {[r.firstName, r.lastName].filter(Boolean).join(" ") || <span className="text-gray-300 italic">Unknown</span>}
                    </div>
                    <div className="text-xs text-gray-400">{r.email ?? ""}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-semibold">{r.repCode}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {r.printedName || <span className="text-gray-300 italic">Not recorded</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {new Date(r.acceptedAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-gray-500">v{r.agreementVersion}</span>
                  </td>
                  <td className="px-4 py-3">
                    {r.signedDocKey ? (
                      <div className="flex items-center gap-3">
                        <a
                          href={`/api/files/${r.signedDocKey}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#1A6FD4] underline flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" /> View
                        </a>
                        <a
                          href={`/api/files/${r.signedDocKey}?download=1`}
                          className="text-xs text-gray-500 hover:text-gray-700 underline flex items-center gap-1"
                        >
                          <Download className="w-3 h-3" /> Download
                        </a>
                      </div>
                    ) : (
                      // Legacy v1.0 rows are a bare checkbox click with no
                      // document. Say so rather than offering a dead link.
                      <span className="text-xs text-amber-600" title="Accepted before signed agreements existed">
                        Accepted, unsigned
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/40">
            <span className="text-xs text-gray-400">
              Showing {showingFrom} to {showingTo} of {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0 || query.isFetching}
                className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={showingTo >= total || query.isFetching}
                className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="text-[11px] text-gray-400 mt-3 flex items-center gap-1.5">
        <FileText className="w-3 h-3" />
        Each signed agreement is the approved document with the associate's signature, printed name and date, plus a signature certificate.
      </p>
    </div>
  );
}
